import { useState, useEffect, useRef } from "react";
import { X, Rocket, AlertTriangle, Info, CheckCircle, XCircle, Wrench, Megaphone, Gift, Star } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { supabase } from "@/integrations/supabase/client";
import { useSiteSettings } from "@/hooks/useSiteSettings";

interface SystemBanner {
  id: string;
  message_fr: string;
  message_en: string;
  background_color: string;
  icon: string;
  is_dismissible: boolean;
}

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  rocket: Rocket,
  info: Info,
  warning: AlertTriangle,
  success: CheckCircle,
  error: XCircle,
  maintenance: Wrench,
  megaphone: Megaphone,
  gift: Gift,
  star: Star,
};

export const LaunchBanner = () => {
  const [banner, setBanner] = useState<SystemBanner | null>(null);
  const [dismissedIds, setDismissedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();
  const { settings } = useSiteSettings();
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Load dismissed banner IDs from localStorage
    const dismissed = localStorage.getItem('dismissedBanners');
    if (dismissed) {
      try {
        setDismissedIds(JSON.parse(dismissed));
      } catch {
        setDismissedIds([]);
      }
    }
    
    fetchActiveBanner();
  }, []);

  const fetchActiveBanner = async () => {
    try {
      const { data, error } = await (supabase.from as any)('system_banners')
        .select('id, message_fr, message_en, background_color, icon, is_dismissible')
        .eq('is_active', true)
        .lte('start_date', new Date().toISOString())
        .or('end_date.is.null,end_date.gte.' + new Date().toISOString())
        .order('priority', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        // PGRST116 = no rows returned, which is fine
        console.error('Error fetching banner:', error);
      }
      
      setBanner(data);
    } catch (err) {
      console.error('Error fetching banner:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    if (banner) {
      const newDismissed = [...dismissedIds, banner.id];
      setDismissedIds(newDismissed);
      localStorage.setItem('dismissedBanners', JSON.stringify(newDismissed));
      document.documentElement.style.removeProperty('--banner-height');
    }
  };

  // Expose banner height as CSS variable so Header can offset its sticky position
  useEffect(() => {
    if (!bannerRef.current) return;
    const updateHeight = () => {
      const h = bannerRef.current?.offsetHeight ?? 0;
      document.documentElement.style.setProperty('--banner-height', `${h}px`);
    };
    updateHeight();
    const ro = new ResizeObserver(updateHeight);
    ro.observe(bannerRef.current);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty('--banner-height');
    };
  }, [banner]);

  // Don't render if admin disabled the banner
  if (settings.show_announcement_banner === false) return null;

  // Don't render anything while loading or if no banner
  if (loading || !banner) return null;
  
  // Don't show if user dismissed this banner
  if (dismissedIds.includes(banner.id)) return null;

  const message = language === 'fr' ? banner.message_fr : banner.message_en;
  const IconComponent = ICON_MAP[banner.icon] || Rocket;

  return (
    <>
      {/* Fixed banner at top */}
      <div
        ref={bannerRef}
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
        className={`fixed top-0 left-0 right-0 z-[100] bg-gradient-to-r ${banner.background_color} text-white px-4 py-2 shadow-lg`}
      >
        <div className="container mx-auto flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-1 justify-center text-center">
            <IconComponent className="h-4 w-4 animate-pulse hidden sm:block" />
            <span className="text-sm font-medium">
              {message}
            </span>
          </div>
          {banner.is_dismissible && (
            <button 
              onClick={handleDismiss}
              className="p-1 hover:bg-white/20 rounded-full transition-colors flex-shrink-0"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      {/* Spacer — matches banner height via CSS variable */}
      <div style={{ height: 'var(--banner-height, 0px)' }} />
    </>
  );
};
